import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { CheckmarkCircle02Icon, AlertCircleIcon } from '@hugeicons/core-free-icons';
import { useSearchParams } from 'react-router-dom';
import { useOAuthFinalize } from '../hooks/useOAuthFinalize';
import { Logo } from '@/components/ui/Logo';
import { capture } from '../lib/analytics';
import { retryOAuthConnect } from '../lib/retryOAuthConnect';

const PROVIDER_LABELS: Record<string, string> = {
  slack: 'Slack',
  clickup: 'ClickUp',
  basecamp: 'Basecamp',
  monday: 'monday.com',
  github: 'GitHub',
  jira: 'Jira',
  linear: 'Linear',
  notion: 'Notion',
};

const ERROR_MESSAGES: Record<string, string> = {
  missing_params: 'The authorization request was incomplete. Please try again.',
  invalid_state: 'The authorization session expired. Please try again.',
  token_exchange_failed: 'Could not complete the authorization. Please try again.',
  callback_failed: 'An unexpected error occurred. Please try again.',
  access_denied: 'Access was denied. Please authorize OverlayQA to connect.',
  no_team: 'No team found for your account. Please contact support.',
  storage_failed: 'Could not save the connection. Please try again.',
};

export function IntegrationConnectedPage() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const provider = searchParams.get('provider') || 'Integration';
  const finalize = useOAuthFinalize(provider, error, searchParams.get('success') === 'true');
  const isSuccess = finalize.status === 'success';
  const displayName = PROVIDER_LABELS[provider] || provider.charAt(0).toUpperCase() + provider.slice(1);

  const [countdown, setCountdown] = useState(3);

  // Fire exactly one event per landing: connected or failed. This page also
  // gets the canonical `provider` value (slack|jira|linear|notion) which the
  // server-side OAuth-complete callback knows about, so we can build the
  // OAuth funnel: integration_link_clicked -> integration_connected.
  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current || finalize.status === 'pending') return;
    trackedRef.current = true;
    if (isSuccess) {
      capture('integration_connected', { provider });
    } else {
      capture('integration_connect_failed', { provider, error: error || (finalize.message ? 'finalize_failed' : 'unknown') });
    }
  }, [isSuccess, provider, error, finalize.status, finalize.message]);

  useEffect(() => {
    if (!isSuccess) return;

    // Keep the state updater pure: StrictMode may invoke it twice.
    const timer = setInterval(() => setCountdown((prev) => Math.max(0, prev - 1)), 1000);
    const closeTimer = setTimeout(() => window.close(), 3000);
    return () => { clearInterval(timer); clearTimeout(closeTimer); };
  }, [isSuccess]);

  // Sends the OPENER to the connect cards and closes this popup. The
  // destination and the reason it cannot be an in-popup navigation both live in
  // retryOAuthConnect.ts; keep them there so this page and FigmaConnectedPage
  // cannot drift apart again.
  const handleRetry = () => {
    retryOAuthConnect();
  };

  const errorMessage = finalize.message || (error && ERROR_MESSAGES[error]
    ? ERROR_MESSAGES[error]
    : `Something went wrong${error ? `: ${error}` : ''}. Please try again.`);

  return (
    <div className="auth-shell flex min-h-screen items-center justify-center px-4">
      <div className="auth-shell__panel">
        <div className="space-y-6 text-center">
          <Logo showText size="lg" className="justify-center" />

          {finalize.status === 'pending' ? (
            <div className="space-y-4" role="status">
              <h2 className="hero-title">Finishing your connection...</h2>
              <p className="text-sm text-gray-500">Keep this tab open while we save your connection.</p>
              {finalize.canSignIn && (
                <>
                  <p className="text-sm text-gray-500">Waiting for the OverlayQA extension. You can also sign in here to continue.</p>
                  <a className="auth-card__submit" href={finalize.signInHref}><span className="text-cap-trim">Sign in to finish</span></a>
                </>
              )}
            </div>
          ) : isSuccess ? (
            <>
              <div className="flex justify-center">
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={56} strokeWidth={2} color="currentColor" className="text-teal-400 animate-fade-in" />
              </div>
              <div className="space-y-2 animate-slide-up">
                <h2 className="hero-title">
                  {displayName} connected
                </h2>
                <p className="text-sm text-gray-500">
                  You can close this tab and return to the OverlayQA dashboard.
                </p>
                <p className="text-xs text-gray-400 mt-3">
                  Closing automatically in {countdown}s...
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-center">
                <HugeiconsIcon icon={AlertCircleIcon} size={56} strokeWidth={2} color="currentColor" className="text-red-500 animate-fade-in" />
              </div>
              <div className="space-y-2 animate-slide-up">
                <h2 className="hero-title">
                  Connection failed
                </h2>
                <p className="text-sm text-gray-500">
                  {errorMessage}
                </p>
              </div>
              {finalize.canSignIn && <a className="auth-card__submit" href={finalize.signInHref}><span className="text-cap-trim">Sign in to finish</span></a>}
              <button
                onClick={handleRetry}
                className="auth-card__submit"
              >
                <span className="text-cap-trim">Try again</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
